import React, { useEffect } from "react";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const SUCCESS_DISPLAY_DURATION_MS = 400;

const scan = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0; }
  15%, 85% { opacity: 1; }
  90% { transform: translateY(94px); }
`;
const highlight = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
`;
const reveal = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

const ClassifyLoading = ({
  complete = false,
  rowCount = 0,
  onComplete,
  itemLabel = "row",
  itemLabelPlural = `${itemLabel}s`,
}) => {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  useEffect(() => {
    if (!complete || !onComplete) return undefined;
    const timer = setTimeout(
      onComplete,
      reduceMotion ? 0 : SUCCESS_DISPLAY_DURATION_MS
    );
    return () => clearTimeout(timer);
  }, [complete, onComplete, reduceMotion]);

  return (
    <Stack
      spacing={1.5}
      alignItems='center'
      role='status'
      aria-live='polite'
      aria-atomic='true'
      sx={{ minWidth: 0, px: 1, py: { xs: 2, sm: 3 }, textAlign: "center" }}
    >
      <Box
        aria-hidden='true'
        sx={{
          position: "relative",
          width: 168,
          height: 128,
          flexShrink: 0,
          border: "1px solid",
          borderColor: complete ? "success.main" : "divider",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {complete ? (
          <Box sx={{ height: "100%", display: "grid", placeItems: "center", bgcolor: (theme) => alpha(theme.palette.success.main, 0.06) }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "success.main", animation: reduceMotion ? "none" : `${reveal} 300ms ease-out both` }} />
          </Box>
        ) : (
          <>
            <Stack direction='row' alignItems='center' spacing={0.75} sx={{ px: 1.5, pt: 1.25, pb: 0.75, color: "primary.main" }}>
              <AutoAwesomeIcon sx={{ fontSize: 18 }} />
              <Box sx={{ height: 4, width: 64, bgcolor: "divider", borderRadius: 1 }} />
            </Stack>
            <Stack spacing={1.25} sx={{ px: 1.5, pt: 1 }}>
              {[0, 1, 2, 3].map((row) => (
                <Stack key={row} direction='row' spacing={1} alignItems='center' sx={{ animation: reduceMotion ? "none" : `${highlight} 2.4s ease-in-out infinite`, animationDelay: `${row * 0.3}s` }}>
                  <Box sx={{ width: 6, height: 6, bgcolor: "secondary.main", borderRadius: 0.5 }} />
                  <Box sx={{ height: 4, width: row % 2 ? 60 : 76, bgcolor: "divider", borderRadius: 1 }} />
                  <Box sx={{ height: 4, flex: 1, bgcolor: "primary.main", borderRadius: 1 }} />
                </Stack>
              ))}
            </Stack>
            {!reduceMotion && <Box sx={{ position: "absolute", top: 18, left: 0, right: 0, height: 2, bgcolor: "primary.main", animation: `${scan} 2.4s ease-in-out infinite` }} />}
          </>
        )}
      </Box>
      <Box sx={{ maxWidth: 420 }}>
        <Typography variant='h6' fontWeight={700} sx={{ mb: 0.5 }}>
          {complete ? "Classification complete" : `Classifying ${itemLabelPlural}`}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {complete
            ? "Your results are ready to review."
            : `AI is reviewing ${rowCount > 0 ? `${rowCount} selected ${itemLabelPlural}` : `your ${itemLabelPlural}`} using your processing options.`}
        </Typography>
      </Box>
    </Stack>
  );
};

export default ClassifyLoading;
